<template>
    <div class="content-wrapper">
        <app-page-top-section :title="$t('transaction_imports')">
            <template v-if="batchId">
                <app-default-button 
                    class="mr-2 btn-success"
                    :title="$t('start_sync')"
                    @click="startSync"
                    v-if="$can('create_finance_transactions')"
                />
            </template>
        </app-page-top-section>

        <app-table
            id="finance-transaction-import-table"
            :options="options"
            @action="triggerActions"
        />

        <app-confirmation-modal
            v-if="confirmationModalActive"
            icon="trash-2"
            modal-id="app-confirmation-modal"
            @confirmed="confirmed('finance-transaction-import-table')"
            @cancelled="cancelled"
        />
    </div>
</template>

<script>
import HelperMixin from "../../../../common/Mixin/Global/HelperMixin";
import FinanceTransactionImportMixin from "../../Mixins/FinanceTransactionImportMixin";
import {TENANT_BASE_URL} from "../../../../common/Config/UrlHelper";
import {axiosGet, axiosPost, axiosDelete, axiosPatch} from "../../../../common/Helper/AxiosHelper";

export default {
    name  : "FinanceTransactionImportList",
    mixins: [HelperMixin, FinanceTransactionImportMixin],
    data() {
        return {
            batchId: null,
            batchStats: null,
        }
    },
    mounted() {
        // Load finance categories for the category dropdown
        this.loadCategories();
        
        // Get batch_id from URL query string
        const urlParams = new URLSearchParams(window.location.search);
        this.batchId = urlParams.get('batch_id') || null;
        
        if (this.batchId) {
            // Add batch_id as default filter
            this.options.url = `${TENANT_BASE_URL}app/finance-transaction-imports?batch_id=${this.batchId}`;
            this.loadBatchStats();
        }

        // Add event listeners for inline editing
        this.$nextTick(() => {
            this.attachInlineEditListeners();
        });

        // Re-attach listeners after table reload
        this.$hub.$on('reload-finance-transaction-import-table', () => {
            this.$nextTick(() => {
                this.attachInlineEditListeners();
            });
        });
    },
    beforeDestroy() {
        this.$hub.$off('reload-finance-transaction-import-table');
    },
    methods: {
        triggerActions(row, action, active) {
            if (action.name === 'confirm') {
                this.confirmSingleImport(row);
            } else if (action.title === this.$t('edit')) {
                // TODO: Implement edit modal
                this.$toastr.i(this.$t('edit_feature_coming_soon'));
            } else {
                this.getAction(row, action, active)
            }
        },
        loadBatchStats() {
            axiosGet(`${TENANT_BASE_URL}app/finance-transaction-imports/batch/${this.batchId}/stats`)
                .then(({data}) => {
                    this.batchStats = data;
                })
                .catch(error => {
                    this.$toastr.e(error.response?.data?.message || 'Failed to load batch stats');
                });
        },
        confirmSingleImport(row) {
            this.$toastr.i(this.$t('confirming_import'));
            
            axiosPost(`${TENANT_BASE_URL}app/finance-transaction-imports/confirm`, {
                import_ids: [row.id]
            })
            .then(({data}) => {
                this.$toastr.s(data.message);
                this.$hub.$emit('reload-finance-transaction-import-table');
                if (this.batchId) {
                    this.loadBatchStats();
                }
            })
            .catch(error => {
                this.$toastr.e(error.response?.data?.message || 'Failed to confirm import');
            });
        },
        startSync() {
            this.$toastr.i(this.$t('syncing_transactions'));
            
            axiosPost(`${TENANT_BASE_URL}app/finance-transaction-imports/sync`, {
                batch_id: this.batchId
            })
            .then(({data}) => {
                if (data) {
                    this.$toastr.s(data.message);
                    this.$hub.$emit('reload-finance-transaction-import-table');
                    this.loadBatchStats();
                }
            })
            .catch(error => {
                this.$toastr.e(error.response?.data?.message || 'Failed to sync transactions');
            });
        },
        deleteBatch() {
            if (!confirm(this.$t('confirm_delete_batch_message'))) {
                return;
            }

            this.$toastr.i(this.$t('deleting_batch'));
            
            axiosDelete(`${TENANT_BASE_URL}app/finance-transaction-imports/batch/${this.batchId}`)
                .then(({data}) => {
                    this.$toastr.s(data.message);
                    this.$router.push('/finance/accounts');
                })
                .catch(error => {
                    this.$toastr.e(error.response?.data?.message || 'Failed to delete batch');
                });
        },
        attachInlineEditListeners() {
            // Handle description input - store original value on focus
            $(document).off('focus', '.editable-description');
            $(document).on('focus', '.editable-description', (e) => {
                const $input = $(e.target);
                $input.data('original-value', $input.val());
            });

            // Handle description input blur - only update if value changed
            $(document).off('blur', '.editable-description');
            $(document).on('blur', '.editable-description', (e) => {
                const $input = $(e.target);
                const rowId = $input.data('row-id');
                const newValue = $input.val();
                const originalValue = $input.data('original-value');
                
                if (newValue !== originalValue) {
                    this.updateImportField(rowId, 'description', newValue);
                }
            });

            // Handle category select change - save immediately when selection changes
            $(document).off('change', '.editable-category');
            $(document).on('change', '.editable-category', (e) => {
                const $select = $(e.target);
                const rowId = $select.data('row-id');
                const newValue = $select.val();
                this.updateImportField(rowId, 'category_id', newValue);
            });
        },
        updateImportField(rowId, field, value) {
            const data = {
                [field]: value
            };

            // When category is selected, also set status to confirmed
            const isSettingConfirmed = field === 'category_id' && value;
            if (isSettingConfirmed) {
                data.status = 'confirmed';
            }

            axiosPatch(`${TENANT_BASE_URL}app/finance-transaction-imports/${rowId}`, data)
                .then(({data}) => {
                    this.$toastr.s(this.$t('updated_successfully'));
                    // Update status badge in DOM directly if status changed to confirmed
                    if (isSettingConfirmed) {
                        const $row = $(`.editable-category[data-row-id="${rowId}"]`).closest('tr');
                        const $statusCell = $row.find('td').eq(-2); // Status is second to last column
                        $statusCell.html(this.getStatusBadge('confirmed', null));
                    }
                })
                .catch(error => {
                    this.$toastr.e(error.response?.data?.message || 'Failed to update');
                    // Reload table to revert changes
                    this.$hub.$emit('reload-finance-transaction-import-table');
                });
        }
    }
}
</script>

<style scoped>
::v-deep .badge-warning {
    background-color: #ffc107 !important;
    color: #000 !important;
}

::v-deep .badge-danger {
    background-color: #dc3545 !important;
    color: #fff !important;
}

::v-deep .badge-success {
    background-color: #28a745 !important;
    color: #fff !important;
}

::v-deep .badge-info {
    background-color: #17a2b8 !important;
    color: #fff !important;
}
</style>
